import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, Timestamp, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'react-toastify';
import { useAuth } from '../Auth/Auth';
import logger from '../../utils/logger';
import { formatCurrency } from '../Help/helper';
import BreadCrumb from '../BreadCrumb';
import {
  DocumentPlusIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  BanknotesIcon,
  ShoppingCartIcon
} from '@heroicons/react/24/outline';

const GeneratePO = () => {
  const { procurementId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [procurement, setProcurement] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const [formData, setFormData] = useState({
    supplier: '',
    supplierAddress: '',
    supplierPhone: '',
    supplierEmail: '',
    deliveryDate: '',
    paymentTerms: '',
    notes: '',
  });

  const pages = [
    { name: 'Procurement', to: '/procurement', component: Link, current: false },
    { name: 'Requests', to: '/procurement/requests', component: Link, current: false },
    { name: 'Generate PO', to: '#', component: 'span', current: true },
  ];

  useEffect(() => {
    fetchProcurement();
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procurementId]);

  const fetchProcurement = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'procurements', procurementId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() };
        setProcurement(data);

        if (data.supplier) {
          setFormData(prev => ({
            ...prev,
            supplier: data.supplier,
          }));
        }
      } else {
        toast.error('Procurement not found');
        navigate('/procurement/requests');
      }
    } catch (error) {
      logger.error('Error fetching procurement:', error);
      toast.error('Failed to load procurement');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'suppliers'));
      const suppliersList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSuppliers(suppliersList);
    } catch (error) {
      logger.error('Error fetching suppliers:', error);
    }
  };

  const handleSupplierChange = (e) => {
    const supplierName = e.target.value;
    setFormData({ ...formData, supplier: supplierName });

    const selectedSupplier = suppliers.find((s) => s.companyName === supplierName);
    if (selectedSupplier) {
      setFormData({
        ...formData,
        supplier: supplierName,
        supplierAddress: selectedSupplier.address || '',
        supplierPhone: selectedSupplier.phoneNumber || '',
        supplierEmail: selectedSupplier.email || '',
      });
    }
  };

  const generatePONumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(1000 + Math.random() * 9000);
    return `PO-${year}${month}-${random}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.supplier) {
      toast.error('Please select or enter a supplier');
      return;
    }

    if (!formData.deliveryDate) {
      toast.error('Please specify required delivery date');
      return;
    }

    try {
      setGenerating(true);

      const existingPOQuery = query(
        collection(db, 'purchaseOrders'),
        where('procurementId', '==', procurementId)
      );
      const existingPOSnapshot = await getDocs(existingPOQuery);

      if (!existingPOSnapshot.empty) {
        toast.error('A purchase order already exists for this procurement');
        return;
      }

      const poData = {
        poNumber: generatePONumber(),
        procurementId: procurement.id,
        procurementNumber: procurement.procurementNumber,
        supplier: formData.supplier,
        supplierAddress: formData.supplierAddress,
        supplierPhone: formData.supplierPhone,
        supplierEmail: formData.supplierEmail,
        items: procurement.cartItems || [],
        subtotal: procurement.subtotal || 0,
        taxes: procurement.taxes || 0,
        grandTotal: procurement.grandTotal || 0,
        deliveryDate: formData.deliveryDate,
        paymentTerms: formData.paymentTerms || 'As agreed',
        notes: formData.notes,
        status: 'Draft',
        createdAt: Timestamp.now(),
        createdBy: currentUser?.displayName || currentUser?.email || 'System',
        lastUpdated: Timestamp.now(),
        statusHistory: [
          {
            status: 'Draft',
            date: Timestamp.now(),
            updatedBy: currentUser?.displayName || currentUser?.email || 'System',
          },
        ],
      };

      const docRef = await addDoc(collection(db, 'purchaseOrders'), poData);

      toast.success('Purchase Order generated successfully!');
      navigate(`/procurement/purchase-orders/${docRef.id}`);
    } catch (error) {
      logger.error('Error generating PO:', error);
      toast.error('Failed to generate purchase order');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-cyan-600 py-4">
        <BreadCrumb pages={pages} />
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </div>
    );
  }

  if (!procurement) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-cyan-600 py-4">
        <BreadCrumb pages={pages} />
        <div className="text-center py-12">
          <DocumentPlusIcon className="h-16 w-16 text-white mx-auto mb-4" />
          <p className="text-white font-medium">Procurement not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-cyan-600 py-4">
      <BreadCrumb pages={pages} />
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl">
              <DocumentPlusIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-gray-900">Generate Purchase Order</h1>
              <p className="text-xs text-gray-600 mt-0.5">
                Create a formal purchase order for procurement #{procurement.procurementNumber}
              </p>
            </div>
          </div>
        </div>

        {/* Procurement Summary */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-cyan-600 -m-4 p-4 rounded-t-xl">
            <ShoppingCartIcon className="h-5 w-5 text-white" />
            <h2 className="text-sm font-black text-white">Procurement Summary</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <div>
              <span className="text-xs font-medium text-gray-600">Procurement #</span>
              <p className="text-xs font-black text-gray-900 mt-0.5">{procurement.procurementNumber}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-600">Status</span>
              <p className="text-xs font-black text-gray-900 mt-0.5">{procurement.status}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-600">Total Amount</span>
              <p className="text-xs font-black text-blue-600 mt-0.5">{formatCurrency(procurement.grandTotal)}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-600">Items</span>
              <p className="text-xs font-black text-gray-900 mt-0.5">{procurement.cartItems?.length || 0}</p>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Left Column - Form (60%) */}
          <div className="lg:col-span-3 space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Supplier Information */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-cyan-600 -m-4 p-4 rounded-t-xl">
                  <BuildingOfficeIcon className="h-5 w-5 text-white" />
                  <h3 className="text-sm font-black text-white">Supplier Information</h3>
                </div>
                <div className="space-y-3 mt-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Supplier <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.supplier}
                      onChange={handleSupplierChange}
                      required
                      className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a supplier</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.companyName}>
                          {supplier.companyName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Supplier Name (Manual)
                      </label>
                      <input
                        type="text"
                        value={formData.supplier}
                        onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                        placeholder="Or enter manually"
                        className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.supplierPhone}
                        onChange={(e) => setFormData({ ...formData, supplierPhone: e.target.value })}
                        placeholder="Contact number"
                        className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={formData.supplierEmail}
                        onChange={(e) => setFormData({ ...formData, supplierEmail: e.target.value })}
                        placeholder="contact@supplier.com"
                        className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <input
                        type="text"
                        value={formData.supplierAddress}
                        onChange={(e) => setFormData({ ...formData, supplierAddress: e.target.value })}
                        placeholder="Supplier address"
                        className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery & Payment */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-cyan-600 -m-4 p-4 rounded-t-xl">
                  <CalendarIcon className="h-5 w-5 text-white" />
                  <h3 className="text-sm font-black text-white">Delivery & Payment Terms</h3>
                </div>
                <div className="space-y-3 mt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Required Delivery Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={formData.deliveryDate}
                        onChange={(e) => setFormData({ ...formData, deliveryDate: e.target.value })}
                        required
                        min={new Date().toISOString().split('T')[0]}
                        className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Payment Terms
                      </label>
                      <select
                        value={formData.paymentTerms}
                        onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
                        className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select payment terms</option>
                        <option value="Cash on Delivery">Cash on Delivery</option>
                        <option value="Net 7 Days">Net 7 Days</option>
                        <option value="Net 15 Days">Net 15 Days</option>
                        <option value="Net 30 Days">Net 30 Days</option>
                        <option value="50% Advance, 50% on Delivery">50% Advance, 50% on Delivery</option>
                        <option value="As agreed">As agreed</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Additional Notes
                    </label>
                    <textarea
                      rows={3}
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Any special instructions or requirements..."
                      className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2">
                <Link
                  to="/procurement/requests"
                  className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-xl shadow-sm hover:bg-gray-50 transition-all duration-200"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={generating}
                  className="px-4 py-2 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50"
                >
                  {generating ? 'Generating...' : 'Generate Purchase Order'}
                </button>
              </div>
            </form>
          </div>

          {/* Right Column - Preview (40%) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Items Preview */}
            <div className="bg-white rounded-xl shadow-lg p-4">
              <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-cyan-600 -m-4 p-4 rounded-t-xl">
                <ShoppingCartIcon className="h-5 w-5 text-white" />
                <h3 className="text-sm font-black text-white">Items to Order</h3>
              </div>
              <div className="space-y-2 mt-4 max-h-96 overflow-y-auto">
                {(procurement.cartItems || []).map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-xs font-bold text-gray-900">{item.description}</p>
                      <p className="text-xs text-gray-600">Qty: {item.quantity} @ {formatCurrency(item.price)}</p>
                    </div>
                    <p className="text-xs font-black text-gray-900">
                      {formatCurrency(item.price * item.quantity)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="bg-white rounded-xl shadow-lg p-4">
              <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-cyan-600 -m-4 p-4 rounded-t-xl">
                <BanknotesIcon className="h-5 w-5 text-white" />
                <h3 className="text-sm font-black text-white">Order Totals</h3>
              </div>
              <dl className="space-y-2 mt-4">
                <div className="flex justify-between">
                  <dt className="text-xs font-medium text-gray-600">Subtotal</dt>
                  <dd className="text-xs font-bold text-gray-900">{formatCurrency(procurement.subtotal)}</dd>
                </div>
                {procurement.taxes > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-xs font-medium text-gray-600">Tax (7.5%)</dt>
                    <dd className="text-xs font-bold text-gray-900">{formatCurrency(procurement.taxes)}</dd>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <dt className="text-sm font-black text-gray-900">Grand Total</dt>
                  <dd className="text-sm font-black text-blue-600">{formatCurrency(procurement.grandTotal)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneratePO;
